/**
 * Vendor Conversation API
 * Handles API calls for vendors to access and participate in conversations
 */

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Get conversations for a vendor
 * @param {string} vendorId - Vendor ID from Firestore vendors collection
 * @param {Object} options - Query options
 * @param {string} options.conversationType - Filter by conversation type (e.g., 'repair_request')
 * @param {number} options.limit - Number of results (default: 50, max: 100)
 * @param {number} options.offset - Pagination offset (default: 0)
 * @param {boolean} options.includeArchived - Include archived conversations (default: false)
 * @returns {Promise<Object>} Conversations data
 */
export const getVendorConversations = async (vendorId, options = {}) => {
  if (!API_URL) {
    throw new Error("REACT_APP_API_URL not configured");
  }

  const {
    conversationType,
    limit = 50,
    offset = 0,
    includeArchived = false,
  } = options;

  const params = new URLSearchParams({
    userId: vendorId,
    userType: "vendor",
  });

  if (conversationType) params.append("conversationType", conversationType);
  if (limit) params.append("limit", limit.toString());
  if (offset) params.append("offset", offset.toString());
  if (includeArchived) params.append("includeArchived", "true");

  const response = await fetch(
    `${API_URL}/conversations?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      error.error || `Failed to fetch conversations: ${response.status}`
    );
  }

  return response.json();
};

/**
 * Get a specific conversation by ID
 * @param {string} conversationId - Conversation ID
 * @param {string} vendorId - Optional vendor ID for unauthenticated access
 * @returns {Promise<Object>} Conversation data
 */
export const getConversation = async (conversationId, vendorId = null) => {
  if (!API_URL) {
    throw new Error("REACT_APP_API_URL not configured");
  }

  let url = `${API_URL}/conversations/${conversationId}`;
  if (vendorId) {
    url += `?vendorId=${encodeURIComponent(vendorId)}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      error.error || `Failed to fetch conversation: ${response.status}`
    );
  }

  return response.json();
};

/**
 * Send a message as a vendor
 * @param {string} conversationId - Conversation ID
 * @param {string} vendorId - Vendor ID
 * @param {string} message - Message content
 * @param {string[]} images - Optional array of image URLs
 * @returns {Promise<Object>} Message response
 */
export const sendVendorMessage = async (
  conversationId,
  vendorId,
  message,
  images = []
) => {
  if (!API_URL) {
    throw new Error("REACT_APP_API_URL not configured");
  }

  const requestBody = {
    conversationId,
    fromUserId: vendorId,
    fromUserType: "vendor", // CRITICAL: This must be preserved by the backend
    message,
    images: images || [],
  };

  console.log("[vendorConversationAPI] Sending message request:", requestBody);
  console.log(
    '[vendorConversationAPI] ⚠️ CRITICAL: fromUserType is "vendor" - backend MUST preserve this!'
  );

  const response = await fetch(`${API_URL}/conversations/send-message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    console.error("[vendorConversationAPI] Error response:", {
      status: response.status,
      statusText: response.statusText,
      error: errorData,
    });
    throw new Error(
      errorData.error ||
        errorData.message ||
        `Failed to send message: ${response.status}`
    );
  }

  return response.json();
};

/**
 * Search conversations
 * @param {string} vendorId - Vendor ID
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {string} options.conversationType - Filter by conversation type
 * @param {number} options.limit - Number of results (default: 20)
 * @returns {Promise<Object>} Search results
 */
export const searchConversations = async (vendorId, query, options = {}) => {
  if (!API_URL) {
    throw new Error("REACT_APP_API_URL not configured");
  }

  const { conversationType, limit = 20 } = options;

  const response = await fetch(`${API_URL}/conversations/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      userId: vendorId,
      userType: "vendor",
      query,
      conversationType,
      limit,
    }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      error.error || `Failed to search conversations: ${response.status}`
    );
  }

  return response.json();
};

/**
 * Get conversation analytics for a vendor
 * @param {string} vendorId - Vendor ID
 * @returns {Promise<Object>} Analytics data
 */
export const getVendorConversationAnalytics = async (vendorId) => {
  if (!API_URL) {
    throw new Error("REACT_APP_API_URL not configured");
  }

  const response = await fetch(
    `${API_URL}/conversations/analytics/${vendorId}?userType=vendor`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(
      error.error || `Failed to fetch analytics: ${response.status}`
    );
  }

  return response.json();
};

export default {
  getVendorConversations,
  getConversation,
  sendVendorMessage,
  searchConversations,
  getVendorConversationAnalytics,
};
